"use client";
import { atom, useAtom } from "jotai";
import { useEffect } from "react";
import { useElementResize } from "../../hooks/useElementResize";
import { useScreenSize } from "../../hooks/useScreenSize";
import { hwyStore } from "../../utilities/store";

export interface ISticky {
    order: number;
    height: number;
    screen: "all" | "xs";
    position: "top" | "bottom" | "none";
    isSticky: boolean;
}

export const stickyAtom = atom<ISticky[]>([]);

export const useStickyAtom = () => {
    const [stickies, setStickies] = useAtom(stickyAtom, { store: hwyStore });

    const removeSticky = (order: number) => {
        setStickies((stickies) => stickies.filter((s) => s.order !== order));
    };

    return {
        stickies,
        setStickies,
        removeSticky,
    };
};

const useSticky = (
    order: number,
    screen: "all" | "xs",
    position: "top" | "bottom" | "none",
    disclude: number[] = [],
) => {
    const { screenSize } = useScreenSize();
    const { elementRef, height: elementHeight } = useElementResize();
    const { stickies, setStickies, removeSticky } = useStickyAtom();
    const sticky = stickies.find((s) => s.order === order);
    const isSticky = !!sticky?.isSticky;

    const offsetTop = stickies.reduce(
        (a, s) =>
            (s.order < order && s.position === "top" && !disclude.includes(s.order) && s.isSticky
                ? s.height
                : 0) + a,
        0,
    );
    const offsetBottom = stickies.reduce(
        (a, s) => (s.order < order && s.position === "bottom" && s.isSticky ? s.height : 0) + a,
        0,
    );

    const updateSticky = () => {
        const height = elementHeight || elementRef.current?.offsetHeight || 0;

        if (!elementRef.current) return;

        setStickies((prev) => {
            const prevOffsetTop = prev.reduce(
                (a, s) =>
                    s.order < order && s.position === "top" && !disclude.includes(s.order) && s.isSticky
                        ? s.height + a
                        : a,
                0,
            );
            const shouldStickTop =
                position === "top" &&
                window.scrollY + prevOffsetTop >= (elementRef.current!.parentNode as HTMLElement).offsetTop;
            const shouldSticky =
                (position === "bottom" || shouldStickTop) && (screen === "all" || screen === screenSize);
            const existing = prev.find((s) => s.order === order);

            if (
                existing &&
                existing.isSticky === shouldSticky &&
                existing.position === position &&
                existing.screen === screen &&
                existing.height === height
            ) {
                return prev;
            }

            return [
                ...prev.filter((s) => s.order !== order),
                { order, height, position, screen, isSticky: shouldSticky },
            ];
        });
    };

    /**
     * remove sticky on unmount of component
     */
    useEffect(() => {
        return () => removeSticky(order);
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        window.addEventListener("scroll", () => updateSticky(), { signal: controller.signal });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        updateSticky();
    }, [elementHeight, screenSize, position]);

    return {
        isSticky,
        stickyRef: elementRef,
        top: `${offsetTop}px`,
        bottom: `calc(${offsetBottom}px + env(safe-area-inset-bottom, 0px))`,
        height: elementHeight || elementRef.current?.offsetHeight || 0,
    };
};
export default useSticky;
